import React, { useEffect, useState } from "react";
import Helmet from "react-helmet";
import Img from "gatsby-image";
import { useData } from "../hooks/use-data";
import emotionReset from "emotion-reset";
import { Global, css } from "@emotion/react";
import styled from "@emotion/styled";
import Logos from "../assets/logos.svg";

const SiteContainer = styled.main`
  display: grid;
  grid-template-columns: 1fr 3fr;
`;

const TextContainer = styled.div`
  margin-top: 3px;
  margin-left: 3rem;
  max-width: 28rem;
`;

const ConstellationsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-content: flex-start;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const ImageDescription = styled.div`
  position: relative;
  margin-bottom: 2rem;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 12vw;
  min-width: 200px;
  height: fit-content;
  padding: 1rem;
  cursor: pointer;
  p {
    position: absolute;
    bottom: -24px;
    font-size: 2rem;
  }
`;

const DrawerContainer = styled.div`
  position: fixed;
  bottom: 0;
  padding-top: 3px;
  padding-left: 3rem;
  padding-bottom: 6rem;
  width: 100%;
  border-top: 1px solid currentColor;
  background: #191919;
`;

// markup
const IndexPage = () => {
  const { site, allImages, allConstellations } = useData();
  const [isHovered, setIsHovered] = useState(false);
  const [textColour, setTextColour] = useState("white");
  const [constellation, setConstellation] = useState(null);
  const [images, setImages] = useState([...allImages.nodes]);
  const [activeImages, setActiveImages] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (isHovered === false) {
      setImages([...allImages.nodes]);
    }
  }, [isHovered]);

  const findConstellation = (i) => {
    let c = i.data.constellations;
    let cId = c[Math.floor(Math.random() * c.length)].constellation.prismicId;
    let foundC = allConstellations.nodes.find(
      (constellation) => constellation.prismicId === cId
    );
    setConstellation(foundC);
    setTextColour(foundC.data.colour);
    activateImages(cId);
  };

  const activateImages = (cId) => {
    let newActiveImages = [];
    let activeIndex = 1;
    const newImages = images.map((i) => {
      let o = Object.assign({}, i);
      if (
        o.data.constellations.some(
          (cSet) => cSet.constellation.prismicId === cId
        )
      ) {
        o.active = true;
        o.index = activeIndex;
        activeIndex++;
        newActiveImages.push(o);
      } else {
        o.active = false;
      }
      return o;
    });
    setImages(newImages);
    setActiveImages(newActiveImages);
  };

  return (
    <>
      <Helmet>
        <meta name='pinterest' content='nopin' />
      </Helmet>
      <Global
        styles={css`
          ${emotionReset}

          body {
            background-color: #191919;
            font-family: Helvetica, sans-serif;
          }

          p {
            line-height: 1.2;
          }

          *,
          *::after,
          *::before {
            box-sizing: border-box;
            -moz-osx-font-smoothing: grayscale;
            -webkit-font-smoothing: antialiased;
            font-smoothing: antialiased;
            color: ${textColour};
          }
        `}
      />
      <SiteContainer>
        <TextContainer>
          <h1 style={{ fontSize: "2rem", marginBottom: "1.5rem" }}>
            {constellation ? constellation.data.title : site.siteMetadata.title}
          </h1>
          {constellation?.data && (
            <div
              css={css`
                margin-bottom: 2rem;
                padding-bottom: 2rem;
                border-bottom: 1px solid currentColor;
              `}
              dangerouslySetInnerHTML={{
                __html: constellation.data.description.html,
              }}
            ></div>
          )}
          {activeImages.length
            ? activeImages.map((i) => (
                <ImageDescription key={i.prismicId}>
                  <span
                    css={css`
                      display: block;
                      position: absolute;
                      left: -43px;
                      top: -3px;
                      width: 30px;
                      font-size: 2rem;
                      text-align: center;
                    `}
                  >
                    {i.index}
                  </span>
                  <h1
                    css={css`
                      margin-bottom: 0.3rem;
                    `}
                  >
                    {i.data.title}
                  </h1>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: i.data.description.html,
                    }}
                  ></div>
                </ImageDescription>
              ))
            : null}
        </TextContainer>
        <ConstellationsContainer>
          {images.map((i) => {
            return (
              <ImageContainer
                key={i.prismicId}
                onMouseEnter={() => {
                  setIsHovered(true);
                  findConstellation(i);
                  setDrawerOpen(false);
                }}
                onMouseLeave={() => {
                  setIsHovered(false);
                  setConstellation(null);
                  setTextColour("white");
                  setActiveImages([]);
                }}
                css={css`
                  filter: blur(${i.active ? 0 : 10}px)
                    grayscale(${i.active ? 0 : 100}%);
                  opacity: ${i.active ? 1 : 0.4};
                  transition: all 0.5s ease;
                `}
              >
                <Img
                  fluid={i.data.image.fluid}
                  style={{ color: i.active ? "yellow" : "black" }}
                  draggable={false}
                >
                  {i.data.title}
                </Img>
                {i.active && (
                  <p
                    css={css`
                      z-index: 2000;
                    `}
                  >
                    {i.index}
                  </p>
                )}
              </ImageContainer>
            );
          })}
        </ConstellationsContainer>
        <DrawerContainer
          css={css`
            transition: transform 0.5s ease;
            transform: translateY(${drawerOpen ? 0 : 93}%);
          `}
        >
          <span
            css={css`
              display: block;
              font-size: 2rem;
              cursor: pointer;
              margin-bottom: 2rem;
            `}
            onClick={() => {
              setDrawerOpen(!drawerOpen);
            }}
          >
            Information
          </span>
          <span
            css={css`
              display: block;
              position: absolute;
              font-size: 2rem;
              cursor: pointer;
              top: 3px;
              right: 0.5rem;
              transition: opacity 0.5s ease;
              opacity: ${drawerOpen ? 1 : 0};
            `}
            onClick={() => {
              setDrawerOpen(false);
            }}
          >
            Close
          </span>
          <p
            css={css`
              max-width: 29rem;
              margin-bottom: 2rem;
            `}
          >
            These Constellations develop from an approach described in Methods
            for Change" as Elliptical Methodology. This can provide a creative
            and provocative framing for exploratory research studies. Elliptical
            Methodologies can generate novel and unusual configurations, such as
            the constellations demonstrated here. This approach can in turn
            generate new conversations and shift attention towards things — and
            connections between things — that are usually overlooked. They can
            help to highlight areas and topics that have not yet been
            researched, including things that may currently be viewed as
            frivolous, irreverent, unconnected or unimportant within a
            particular discipline, sector or field of study, as well as those
            that would benefit from a fresh perspective. Stephen Walker is
            Professor and Head of Architecture at the University of Manchester.
          </p>
          <p>Unless stated all photos were taken by the author.</p>
          <a
            href='mailto:s.j.walker@manchester.ac.uk'
            style={{ display: "block" }}
          >
            s.j.walker@manchester.ac.uk
          </a>
          <Logos style={{ marginTop: "3rem" }} />
        </DrawerContainer>
      </SiteContainer>
    </>
  );
};

export default IndexPage;
